import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Table,
  Tag,
  Upload,
} from 'antd'
import {
  ArrowRightOutlined,
  LoadingOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { FormikProvider, useFormik } from 'formik'
import { toast } from 'react-toastify'
import type { UploadFile } from 'antd/es/upload/interface'
import type { TurmaListItem } from '@/types'
import { useTurmasStore } from '@/stores/turmasStore'
import { useAuthStore } from '@/stores/authStore'
import { FormikInput } from '@/components/FormikInput'
import { formatCurrency, formatDate, isValidCnpj, isValidCpf } from '@/lib/formatters'
import { abrirChatDoJivo } from '@/lib/contextoDoJivo'
import { buscarEmpresaPorCnpj } from '@/lib/brasilApi'
import { situacaoDaTurma } from '@/lib/situacaoDaTurma'

export function Turmas() {
  return (
    <div className="space-y-5">
      <Cabecalho />
      <AlertaDeSolicitacaoPendente />
      <Resumo />
      <ListaDeTurmas />
      <ModalDeSolicitacao />
    </div>
  )
}

function Cabecalho() {
  const abrirModal = useTurmasStore((state) => state.setModalAberto)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-3xl font-bold leading-tight text-[#1a3a5c]">Turmas</h1>
        <p className="mt-1 text-sm text-slate-500">
          Acompanhe cobranças, alunos e notas fiscais.
        </p>
      </div>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirModal(true)}>
        Solicitar turma
      </Button>
    </div>
  )
}

function AlertaDeSolicitacaoPendente() {
  const temSolicitacao = useTurmasStore((state) => state.solicitacoesPendentes > 0)
  if (!temSolicitacao) return null
  return (
    <Alert
      type="info"
      showIcon
      message="Você tem uma turma em análise"
      description="Nosso suporte está preparando os dados da turma e entrará em contato."
      action={<Button onClick={abrirChatDoJivo}>Falar com o suporte</Button>}
    />
  )
}

function Resumo() {
  const total = useTurmasStore((state) => state.totalRecords)
  const emAnalise = useTurmasStore((state) => state.solicitacoesPendentes)
  return (
    <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
      <Card size="small">
        <span className="text-sm text-slate-500">Turmas</span>
        <div className="text-2xl font-bold text-[#1a3a5c]">{total}</div>
      </Card>
      <Card size="small">
        <span className="text-sm text-slate-500">Em análise</span>
        <div className="text-2xl font-bold text-[#ff6422]">{emAnalise}</div>
      </Card>
    </div>
  )
}

function ListaDeTurmas() {
  const fetchTurmas = useTurmasStore((state) => state.fetchTurmas)
  const erro = useTurmasStore((state) => state.error)
  useEffect(() => {
    void fetchTurmas()
  }, [fetchTurmas])
  if (erro) return <Alert type="error" showIcon message={erro} />
  return (
    <>
      <div className="hidden md:block">
        <TabelaDeTurmas />
      </div>
      <div className="md:hidden">
        <TurmasCards />
      </div>
    </>
  )
}

function TabelaDeTurmas() {
  const navigate = useNavigate()
  const data = useTurmasStore((state) => state.data)
  const loading = useTurmasStore((state) => state.loading)
  const total = useTurmasStore((state) => state.totalRecords)
  const filters = useTurmasStore((state) => state.filters)
  const mudarPagina = useTurmasStore((state) => state.setPage)
  const fetchTurmas = useTurmasStore((state) => state.fetchTurmas)
  return (
    <Table
      rowKey="id"
      dataSource={data}
      loading={loading}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'Nenhuma turma encontrada' }}
      columns={[
        { title: 'Turma', dataIndex: 'descricaoTurma' },
        { title: 'Alunos', dataIndex: 'quantidadeDeAlunos' },
        {
          title: 'Valor total',
          render: (_, turma: TurmaListItem) => valorOuTraco(turma.pagamento?.valor),
        },
        {
          title: 'Repasse',
          render: (_, turma: TurmaListItem) => valorOuTraco(turma.repasseDoParceiro),
        },
        {
          title: 'Valor New Driver',
          render: (_, turma: TurmaListItem) => valorOuTraco(turma.pagamento?.valorNewDriver),
        },
        {
          title: 'Vencimento',
          render: (_, turma: TurmaListItem) => formatDate(turma.pagamento?.dataVencimento),
        },
        {
          title: 'Pagamento',
          render: (_, turma: TurmaListItem) => (
            <TagDeSituacao
              statusTurma={turma.statusTurma}
              dataPagamento={turma.pagamento?.dataPagamento}
              dataVencimento={turma.pagamento?.dataVencimento}
            />
          ),
        },
        {
          title: 'Ações',
          key: 'acoes',
          fixed: 'right',
          width: 100,
          render: (_, turma: TurmaListItem) => (
            <Button type="link" onClick={() => navigate(`/turmas/${turma.id}`)}>
              Detalhes
            </Button>
          ),
        },
      ]}
      pagination={{
        total,
        current: filters.page,
        pageSize: filters.limit,
        showSizeChanger: true,
        onChange: async (page, limit) => {
          mudarPagina(page, limit)
          await fetchTurmas()
        },
      }}
    />
  )
}

function TurmasCards() {
  const navigate = useNavigate()
  const data = useTurmasStore((state) => state.data)
  const total = useTurmasStore((state) => state.totalRecords)
  const filters = useTurmasStore((state) => state.filters)
  const mudarPagina = useTurmasStore((state) => state.setPage)
  const fetchTurmas = useTurmasStore((state) => state.fetchTurmas)
  if (!data.length) return <Empty description="Nenhuma turma encontrada" />
  return (
    <div className="space-y-3">
      {data.map((turma) => (
        <Card
          key={turma.id}
          title={turma.descricaoTurma}
          extra={
            <TagDeSituacao
              statusTurma={turma.statusTurma}
              dataPagamento={turma.pagamento?.dataPagamento}
              dataVencimento={turma.pagamento?.dataVencimento}
            />
          }
        >
          <div>{turma.quantidadeDeAlunos} alunos</div>
          <div>Valor total: {valorOuTraco(turma.pagamento?.valor)}</div>
          <div>Seu repasse: {valorOuTraco(turma.repasseDoParceiro)}</div>
          <Button type="link" onClick={() => navigate(`/turmas/${turma.id}`)}>
            Detalhes <ArrowRightOutlined />
          </Button>
        </Card>
      ))}
      <Pagination
        simple
        align="center"
        total={total}
        current={filters.page}
        pageSize={filters.limit}
        onChange={async (page, limit) => {
          mudarPagina(page, limit)
          await fetchTurmas()
        }}
      />
    </div>
  )
}

function TagDeSituacao({
  statusTurma,
  dataPagamento,
  dataVencimento,
}: {
  statusTurma: 'SOLICITADA' | 'ATIVA'
  dataPagamento?: string | null
  dataVencimento?: string | null
}) {
  const situacao = situacaoDaTurma({ statusTurma, dataPagamento, dataVencimento })
  return <Tag color={situacao.cor}>{situacao.texto}</Tag>
}

function ModalDeSolicitacao() {
  const aberto = useTurmasStore((state) => state.modalAberto)
  const setAberto = useTurmasStore((state) => state.setModalAberto)
  const solicitar = useTurmasStore((state) => state.solicitar)
  const parceiro = useAuthStore((state) => state.parceiro)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [buscandoCnpj, setBuscandoCnpj] = useState(false)
  const formik = useFormik({
    initialValues: {
      nomeTurma: '',
      observacoes: '',
      pagadorCpfCnpj: '',
      pagadorNome: '',
      pagadorEhOParceiro: false,
      emitirNotaParaPagador: false,
    },
    validate: (values) => {
      const errors: Partial<Record<keyof typeof values, string>> = {}
      if (!values.nomeTurma.trim()) errors.nomeTurma = 'Obrigatório'
      const documento = values.pagadorCpfCnpj.replace(/\D/g, '')
      if (
        !values.pagadorEhOParceiro &&
        documento &&
        !(
          (documento.length === 11 && isValidCpf(documento)) ||
          (documento.length === 14 && isValidCnpj(documento))
        )
      ) {
        errors.pagadorCpfCnpj = 'CPF/CNPJ inválido'
      }
      if (!values.pagadorEhOParceiro && !values.pagadorNome.trim()) {
        errors.pagadorNome = 'Obrigatório'
      }
      return errors
    },
    onSubmit: async (values, helpers) => {
      try {
        await solicitar(values, arquivo)
        setAberto(false)
        setArquivo(null)
        helpers.resetForm()
      } catch {
        toast.error('Não foi possível enviar a solicitação.')
      }
    },
  })

  function alternarPagadorEhParceiro(marcado: boolean) {
    void formik.setFieldValue('pagadorEhOParceiro', marcado)
    if (marcado && parceiro) {
      void formik.setFieldValue('pagadorCpfCnpj', parceiro.cpfCnpj)
      void formik.setFieldValue('pagadorNome', parceiro.nome)
    }
  }

  async function buscarCnpj(): Promise<void> {
    const cnpj = formik.values.pagadorCpfCnpj.replace(/\D/g, '')
    if (cnpj.length !== 14 || !isValidCnpj(cnpj)) return
    try {
      setBuscandoCnpj(true)
      const empresa = await buscarEmpresaPorCnpj(cnpj)
      if (!empresa) {
        toast.error('CNPJ não encontrado na BrasilAPI.')
        return
      }
      if (!formik.values.pagadorNome.trim()) {
        await formik.setFieldValue('pagadorNome', empresa.razaoSocial)
      }
    } catch {
      toast.error('Não foi possível consultar o CNPJ na BrasilAPI.')
    } finally {
      setBuscandoCnpj(false)
    }
  }

  const listaDeArquivos: UploadFile[] = arquivo
    ? [{ uid: arquivo.name, name: arquivo.name, status: 'done' }]
    : []

  return (
    <Modal
      title="Solicitar turma"
      open={aberto}
      onCancel={() => setAberto(false)}
      onOk={() => formik.submitForm()}
      okText="Enviar solicitação"
      confirmLoading={formik.isSubmitting}
      destroyOnHidden
    >
      <FormikProvider value={formik}>
        <Form layout="vertical" className="pt-3">
          <FormikInput name="nomeTurma" label="Nome da turma" />
          <Form.Item label="Lista de alunos (opcional)">
            <Upload.Dragger
              accept=".pdf,.csv,.xls,.xlsx,.png,.jpg,.jpeg"
              beforeUpload={(file) => {
                setArquivo(file)
                return false
              }}
              maxCount={1}
              fileList={listaDeArquivos}
              onRemove={() => setArquivo(null)}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Clique ou arraste o arquivo para esta área</p>
              <p className="ant-upload-hint">PDF, CSV, XLS, XLSX, PNG ou JPG de até 10 MB</p>
            </Upload.Dragger>
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={formik.values.pagadorEhOParceiro}
              onChange={(event) => alternarPagadorEhParceiro(event.target.checked)}
            >
              Quem paga sou eu
            </Checkbox>
          </Form.Item>
          <FormikInput
            name="pagadorCpfCnpj"
            label="CPF/CNPJ do pagador (opcional)"
            mask="cpf-cnpj"
            disabled={formik.values.pagadorEhOParceiro}
            onBlur={buscarCnpj}
            suffix={buscandoCnpj ? <LoadingOutlined spin /> : undefined}
            extra="Ao informar um CNPJ válido, os dados são consultados na BrasilAPI."
          />
          <FormikInput
            name="pagadorNome"
            label="Nome do pagador"
            disabled={formik.values.pagadorEhOParceiro}
          />
          <Form.Item>
            <Checkbox
              checked={formik.values.emitirNotaParaPagador}
              onChange={(event) =>
                formik.setFieldValue('emitirNotaParaPagador', event.target.checked)
              }
            >
              Emitir nota fiscal para este pagador
            </Checkbox>
          </Form.Item>
          <Form.Item label="Observações">
            <Input.TextArea
              name="observacoes"
              value={formik.values.observacoes}
              onChange={formik.handleChange}
              rows={3}
            />
          </Form.Item>
        </Form>
      </FormikProvider>
    </Modal>
  )
}

function valorOuTraco(valor: number | null | undefined): string {
  return valor == null ? '—' : formatCurrency(valor)
}
